import {
  EMPTY_ARRAY,
  getDisplayNameOrNameLabel,
  getModeDrawerOptions,
  IconName,
  ModeEnum,
  toBig,
  type ColumnDef,
  type Customer,
  type CustomerSecurity,
  type EntityAdminRecord,
  type InputsAndDropdownsDrawerDropdownOption,
  type InputsAndDropdownsDrawerOptions,
  type ProductTypeEnum,
  type Security,
  type UnifiedLiquidityResponse,
} from '@talos/kyoko';
import type { ValueGetterParams } from 'ag-grid-community';
import type Big from 'big.js';

type CustomerSecurityRecordBase = Partial<Omit<CustomerSecurity, 'Symbol' | 'SizeBuckets'>> &
  Pick<CustomerSecurity, 'Symbol'>;

export type CustomerSecurityDrawerRecord = CustomerSecurityRecordBase & {
  SizeBuckets?: string;
};

export type CustomerSecuritySaveRecord = CustomerSecurityRecordBase & {
  SizeBuckets?: string[];
};

export type CustomerSecurityCustomer = { Name: Customer['Name']; DisplayName?: Customer['DisplayName'] };

const FILL_INCREMENT_COLUMNS: ColumnDef<CustomerSecurity>[] = [
  {
    type: 'text',
    title: 'Min Size Fill Increment',
    field: 'MinSizeFillIncrement',
    initialWidth: 140,
  },
  {
    type: 'text',
    title: 'Min Amt Fill Increment',
    field: 'MinAmtFillIncrement',
    initialWidth: 160,
  },
];

const USE_UNIFIED_LIQUIDITY_COLUMNS: ColumnDef<CustomerSecurity>[] = [
  {
    type: 'toggle',
    field: 'UnifiedLiquidity',
    title: 'Unified Liquidity',
    description: 'Leverage Unified Liquidity for pricing and execution',
    initialWidth: 130,
    // We only allow editing the unified liquidity value from the drawer.
    params: { isDisabled: () => true },
  },
];

const USE_UNDERLYING_SECURITY_FOR_RFQ_COLUMNS: ColumnDef<CustomerSecurity>[] = [
  {
    type: 'text',
    field: 'UseUnderlyingSecurityForRFQ',
    title: 'Use Underlying Security For RFQ',
    initialWidth: 200,
  },
];

const CUSTOMER_SECURITY_SYMBOL_COLUMN: ColumnDef<CustomerSecurity> = {
  type: 'security',
  field: 'Symbol',
  initialSort: '+',
  initialWidth: 200,
};

const CUSTOMER_SECURITY_COUNTERPARTY_COLUMNS: ColumnDef<CustomerSecurity>[] = [
  { type: 'text', field: 'Counterparty', title: 'Customer', initialHide: true },
];

const CUSTOMER_SECURITY_SETTINGS_COLUMNS: ColumnDef<CustomerSecurity>[] = [
  { type: 'text', field: 'DefaultPricingRuleMode', title: 'Default PR Mode' },
  { type: 'text', field: 'PriceViaSyntheticCross' },
  { type: 'security', field: 'UnderlyingCrossSymbol', title: 'Underlying Symbol' },
  { type: 'size', field: 'MinSizeIncrement', title: 'Size Increment' },
  { type: 'size', field: 'MinAmtIncrement', title: 'Amount Increment' },
  { type: 'size', field: 'MinPriceIncrement', title: 'Price Increment' },
  { type: 'size', field: 'MinimumSize' },
  { type: 'size', field: 'MaximumSize' },
  {
    field: 'SizeBuckets',
    type: 'custom',
    params: {
      valueGetter: ({ data }: ValueGetterParams<CustomerSecurity>) => data?.SizeBuckets?.join(', ') || '',
    },
  },
  { type: 'size', field: 'MinimumAmount' },
  { type: 'text', field: 'DisplaySymbol', initialHide: true },
  { type: 'text', field: 'Description', initialHide: true },
];

export const getCustomerSecurityMasterColumns = (
  showSecurityMasterFillIncrementColumns: boolean,
  showSecurityMasterUnifiedLiquidityColumn: boolean,
  showSecurityMasterUseUnderlyingSecurityForRFQ: boolean,
  enableCounterpartyOverrideCustomerSecurityMaster: boolean
): ColumnDef<CustomerSecurity>[] => {
  return [
    CUSTOMER_SECURITY_SYMBOL_COLUMN,
    ...(enableCounterpartyOverrideCustomerSecurityMaster ? CUSTOMER_SECURITY_COUNTERPARTY_COLUMNS : []),
    ...CUSTOMER_SECURITY_SETTINGS_COLUMNS,
    ...(showSecurityMasterFillIncrementColumns ? FILL_INCREMENT_COLUMNS : []),
    ...(showSecurityMasterUnifiedLiquidityColumn ? USE_UNIFIED_LIQUIDITY_COLUMNS : []),
    ...(showSecurityMasterUseUnderlyingSecurityForRFQ ? USE_UNDERLYING_SECURITY_FOR_RFQ_COLUMNS : []),
  ];
};

function areQuoteCurrenciesCompatible(
  quoteCurrency1: string | undefined,
  quoteCurrency2: string | undefined,
  unifiedQuoteCurrencyGroups: string[][]
): boolean {
  if (quoteCurrency1 === quoteCurrency2) {
    return true;
  }
  if (quoteCurrency1 == null || quoteCurrency2 == null) {
    return false;
  }
  return unifiedQuoteCurrencyGroups.some(group => group.includes(quoteCurrency1) && group.includes(quoteCurrency2));
}

export function getAvailableCustomerDrawerOptions({
  customers = EMPTY_ARRAY,
  customerSecurityRows = EMPTY_ARRAY,
  symbol,
  currentCounterparty,
}: {
  customers: CustomerSecurityCustomer[] | undefined;
  customerSecurityRows: CustomerSecurity[] | undefined;
  symbol: string;
  currentCounterparty?: string;
}): InputsAndDropdownsDrawerDropdownOption<string>[] {
  const usedCounterparties = new Set(
    customerSecurityRows.flatMap(row =>
      row.Symbol === symbol && isCustomerSecurityChildRow(row) ? [row.Counterparty] : EMPTY_ARRAY
    )
  );

  return customers.flatMap(customer => {
    const isCurrentCounterparty = customer.Name === currentCounterparty;
    const isUsedByAnotherOverride = usedCounterparties.has(customer.Name) && !isCurrentCounterparty;
    if (isUsedByAnotherOverride) {
      return EMPTY_ARRAY;
    }

    return [
      {
        value: customer.Name,
        label: getDisplayNameOrNameLabel(customer),
      },
    ];
  });
}

export function getCustomerSecurityMasterDrawerOptions({
  allSecurities,
  filteredSecuritiesForSymbolDropdown,
  unifiedLiquidityTokenBySymbol,
  allowedProductTypes,
  showSecurityMasterUnifiedLiquidityColumn,
  showSecurityMasterFillIncrementColumns,
  showSecurityMasterUseUnderlyingSecurityForRFQ,
  unifiedQuoteCurrencyGroups,
  customers,
  customerSecurityRows,
}: {
  allSecurities: Security[];
  filteredSecuritiesForSymbolDropdown: Security[];
  unifiedLiquidityTokenBySymbol: Map<string, UnifiedLiquidityResponse>;
  allowedProductTypes: ProductTypeEnum[];
  showSecurityMasterUnifiedLiquidityColumn: boolean;
  showSecurityMasterFillIncrementColumns: boolean;
  showSecurityMasterUseUnderlyingSecurityForRFQ: boolean;
  unifiedQuoteCurrencyGroups: string[][];
  customers: Customer[] | undefined;
  customerSecurityRows: CustomerSecurity[] | undefined;
}): {
  parent: InputsAndDropdownsDrawerOptions<CustomerSecurityDrawerRecord>;
  child: InputsAndDropdownsDrawerOptions<CustomerSecurityDrawerRecord>;
} {
  const securitiesBySymbol = new Map(allSecurities.map(sec => [sec.Symbol, sec]));
  const sharedOptions: InputsAndDropdownsDrawerOptions<CustomerSecurityDrawerRecord> = [
    { field: 'DisplaySymbol', type: 'input' },
    { field: 'Description', type: 'input' },
    { type: 'divider', title: 'Trading Settings' },
    {
      field: 'DefaultPricingRuleMode',
      type: 'dropdown',
      getDropdownOptions: () => getModeDrawerOptions(),
      title: 'Default PR Mode',
      showClear: false,
    },
    {
      field: 'PriceViaSyntheticCross',
      type: 'dropdown',
      getDropdownOptions: () => getModeDrawerOptions(),
      showClear: false,
    },
    {
      field: 'UseUnderlyingSecurityForRFQ',
      type: 'dropdown',
      getDropdownOptions: () => getModeDrawerOptions(),
      title: 'Use Underlying Security For RFQ',
      showClear: false,
      getIsHidden: () => !showSecurityMasterUseUnderlyingSecurityForRFQ,
    },
    {
      field: 'UnderlyingCrossSymbol',
      type: 'symbolSelector',
      title: 'Underlying Symbol',
      getAvailableSecurities: form => {
        const mainSecurity = securitiesBySymbol.get(form.Symbol);
        return allSecurities.filter(
          sec =>
            mainSecurity?.BaseCurrency === sec.BaseCurrency &&
            allowedProductTypes.includes(sec.ProductType) &&
            // [DEAL-624] Quote currencies must match or share a unified quote group
            areQuoteCurrenciesCompatible(mainSecurity?.QuoteCurrency, sec.QuoteCurrency, unifiedQuoteCurrencyGroups)
        );
      },
      // UnderlyingCrossSymbol must be provided when enabling pricing via synthetic cross or using the underlying security for RFQ.
      getIsRequired: form =>
        form.PriceViaSyntheticCross === ModeEnum.Enabled || form.UseUnderlyingSecurityForRFQ === ModeEnum.Enabled,
    },
    {
      field: 'UnifiedLiquidity',
      type: 'dropdown',
      getDropdownOptions: () => getModeDrawerOptions(),
      // [DEAL-6581] When pricing via synthetic cross, UL tokens are keyed by the underlying cross security.
      // Stay editable when already Enabled so the user can turn UL off if the lookup loses its token.
      getIsDisabled: form =>
        form.UnifiedLiquidity !== ModeEnum.Enabled &&
        !isCustomerSecurityUnifiedLiquidityAvailable(form, unifiedLiquidityTokenBySymbol),
      validate: form =>
        form.UnifiedLiquidity === ModeEnum.Enabled &&
        !isCustomerSecurityUnifiedLiquidityAvailable(form, unifiedLiquidityTokenBySymbol)
          ? 'Unified Liquidity is not available for the selected security'
          : undefined,
      showClear: false,
      getIsHidden: () => !showSecurityMasterUnifiedLiquidityColumn,
    },
    { type: 'divider', title: 'Increments' },
    [
      { field: 'MinSizeIncrement', type: 'input', title: 'Size Increment' },
      { field: 'MinAmtIncrement', type: 'input', title: 'Amount Increment' },
      { field: 'MinPriceIncrement', type: 'input', title: 'Price Increment' },
    ],
    [
      {
        field: 'MinSizeFillIncrement',
        type: 'input',
        title: 'Min Size Fill Increment',
        getIsHidden: () => !showSecurityMasterFillIncrementColumns,
      },
      {
        field: 'MinAmtFillIncrement',
        type: 'input',
        title: 'Min Amt Fill Increment',
        getIsHidden: () => !showSecurityMasterFillIncrementColumns,
      },
    ],
    { type: 'divider', title: 'Limits' },
    [
      {
        field: 'MinimumSize',
        type: 'input',
        validate: form => {
          if (!form.MinimumSize) {
            return undefined;
          }
          const min = toBig(form.MinimumSize);
          if (!min || min.lte(0)) {
            return 'Minimum size must be a positive value';
          }
          const max = toBig(form.MaximumSize);
          // MaximumSize of 0 means unbounded, so don't compare against it
          if (max && !max.eq(0) && min.gte(max)) {
            return 'Maximum size must be greater than minimum size';
          }
          return undefined;
        },
      },
      {
        field: 'MaximumSize',
        type: 'input',
        validate: form => {
          if (!form.MaximumSize) {
            return undefined;
          }
          const max = toBig(form.MaximumSize);
          if (!max || max.lt(0)) {
            return 'Maximum size must be a positive value or zero';
          }
          const min = toBig(form.MinimumSize);
          // MaximumSize of 0 means unbounded, so don't compare against it
          if (min && !max.eq(0) && min.gte(max)) {
            return 'Maximum size must be greater than minimum size';
          }
          return undefined;
        },
      },
    ],
    {
      field: 'MinimumAmount',
      type: 'input',
      validate: form => {
        if (!form.MinimumAmount) {
          return undefined;
        }
        const value = toBig(form.MinimumAmount);
        if (!value || value.lte(0)) {
          return 'Minimum amount must be a positive value';
        }
        return undefined;
      },
    },
    {
      field: 'SizeBuckets',
      type: 'input',
      transformDrawerRecordToControl: sizeBuckets => {
        if (Array.isArray(sizeBuckets)) {
          return sizeBuckets.join(', ');
        }
        if (typeof sizeBuckets === 'string') {
          return sizeBuckets;
        }
        return '';
      },
      transformControlToDrawerRecord: (sizeBucketsString: string | undefined) => {
        if (!sizeBucketsString) {
          // Prevent sending [""] as the value when cleared or undefined.
          return EMPTY_ARRAY;
        }
        return sizeBucketsString.split(',').map(size => size.trim());
      },
      validate: form => {
        const buckets = form.SizeBuckets?.trim() ? form.SizeBuckets.split(',').map(s => s.trim()) : [];
        let prev: Big | undefined;
        for (const bucket of buckets) {
          const value = toBig(bucket);
          if (!value || value.lte(0)) {
            return `Size "${bucket}" is invalid. Sizes must be positive.`;
          }
          if (prev && value.lte(prev)) {
            return `Size "${bucket}" is invalid. Sizes must be in increasing order.`;
          }
          prev = value;
        }
        return undefined;
      },
    },
  ];

  const parentOptions: InputsAndDropdownsDrawerOptions<CustomerSecurityDrawerRecord> = [
    {
      field: 'Symbol',
      type: 'symbolSelector',
      getIsRequired: () => true,
      disabledWhenEditing: true,
      disableKeyedOptions: true,
      getAvailableSecurities: () => filteredSecuritiesForSymbolDropdown,
    },
    ...sharedOptions,
  ];

  const childOptions: InputsAndDropdownsDrawerOptions<CustomerSecurityDrawerRecord> = [
    { field: 'Symbol', type: 'input', disabledWhenEditing: true, getIsDisabled: () => true, getIsRequired: () => true },
    {
      field: 'Counterparty',
      type: 'dropdown',
      title: 'Customer',
      getIsRequired: () => true,
      disabledWhenEditing: true,
      getDropdownOptions: form =>
        getAvailableCustomerDrawerOptions({
          customers,
          customerSecurityRows,
          symbol: form.Symbol,
          currentCounterparty: form.Counterparty,
        }),
    },
    ...sharedOptions,
  ];

  return { parent: parentOptions, child: childOptions };
}

export const priceViaSyntheticCrossFilter = {
  key: 'PriceViaSyntheticCross' as const,
  field: 'PriceViaSyntheticCross',
  label: 'Price Via Synthetic Cross',
  control: 'multi-select' as const,
  icon: IconName.ArrowUpDown,
  options: Object.keys(ModeEnum),
  getOptionLabel: (status: string) => status,
};

export function getCustomerSecurityGroupColumnDisplayName(
  entity: CustomerSecurityRecordBase | undefined,
  customersByName: Map<string, CustomerSecurityCustomer> | undefined
): string {
  if (!entity) {
    return '';
  }
  if (isCustomerSecurityChildRow(entity)) {
    const customer = customersByName?.get(entity.Counterparty);
    return customer ? getDisplayNameOrNameLabel(customer) : entity.Counterparty;
  }
  return entity.DisplaySymbol ?? entity.Symbol;
}

export function getCustomerSecurityDeletePath(entity: CustomerSecurityDrawerRecord): string {
  const encodedSymbol = encodeURIComponent(entity.Symbol);
  if (!isCustomerSecurityChildRow(entity)) {
    return `/customer-securities/${encodedSymbol}`;
  }
  return `/customer-securities/${encodedSymbol}?counterparty=${encodeURIComponent(entity.Counterparty)}`;
}

export function getCustomerSecurityEntityForPatchPost(
  entity: CustomerSecurityDrawerRecord | CustomerSecuritySaveRecord
): EntityAdminRecord {
  const payload = removeBlankStringValues(entity);

  if (!isCustomerSecurityChildRow(payload)) {
    return payload;
  }
  const { Mode, ...overridePayload } = payload;
  return overridePayload;
}

function removeBlankStringValues(
  entity: CustomerSecurityDrawerRecord | CustomerSecuritySaveRecord
): CustomerSecurityDrawerRecord | CustomerSecuritySaveRecord {
  const entries = Object.entries(entity).flatMap<[string, unknown]>(([key, value]) => {
    if (typeof value !== 'string') {
      return [[key, value]];
    }

    const trimmedValue = value.trim();
    return trimmedValue === '' ? EMPTY_ARRAY : [[key, trimmedValue]];
  });

  return Object.fromEntries(entries) as CustomerSecurityDrawerRecord | CustomerSecuritySaveRecord;
}

export function isCustomerSecurityChildRow<T extends { Counterparty?: string }>(
  entity: T
): entity is T & { Counterparty: string } {
  return Boolean(entity.Counterparty);
}

/** Symbol used to look up unified liquidity tokens for a customer security row. */
export function getCustomerSecurityUnifiedLiquidityLookupSymbol(
  form: Pick<CustomerSecurityDrawerRecord, 'Symbol' | 'PriceViaSyntheticCross' | 'UnderlyingCrossSymbol'>
): string | undefined {
  if (form.PriceViaSyntheticCross === ModeEnum.Enabled) {
    return form.UnderlyingCrossSymbol || undefined;
  }
  return form.Symbol || undefined;
}

export function isCustomerSecurityUnifiedLiquidityAvailable(
  form: Pick<CustomerSecurityDrawerRecord, 'Symbol' | 'PriceViaSyntheticCross' | 'UnderlyingCrossSymbol'>,
  unifiedLiquidityTokenBySymbol: Map<string, UnifiedLiquidityResponse>
): boolean {
  const lookupSymbol = getCustomerSecurityUnifiedLiquidityLookupSymbol(form);
  return lookupSymbol != null && unifiedLiquidityTokenBySymbol.has(lookupSymbol);
}
